import type { StudentDomain } from '@mathflat/domain/@entities/Student/domain'

import { academyApi } from '~/@common/api/academyApi'
import { curriculumApi } from '~/@common/api/curriculumApi'
import { studentApi } from '~/@common/api/studentApi'
import { userApi } from '~/@common/api/userApi'
import { UserPermission } from '~/@common/models/(Permission)/UserPermission'
import { remoteStorageService } from '~/@common/services/remoteStorage.service'
import { commonRepo } from '~/@common/services/repo.service'
import { envUtils } from '~/@common/utils/envUtils'

import { setI18nLanguagePack } from '../i18n'
import { datadogClient } from './datadog'

export const loadInitialData = async (
  studentId = localStorage.getItem('studentId'),
  restrictions?: string[],
) => {
  if (!studentId) {
    throw Error('studentId가 없습니다.')
  }

  remoteStorageService.initRelationId(studentId)

  try {
    const [academy, appSetting, student, curriculumCore, me] = await Promise.all([
      academyApi.getAcademies(),
      studentApi.getStudentAppSettings(studentId as StudentDomain.Id),
      studentApi.getStudentById(studentId as StudentDomain.Id),
      curriculumApi.getCurriculumCore(),
      userApi.getMe(),
    ])

    commonRepo.setAcademy(academy)

    // 로그인 페이지로 첫 진입한 경우 initI18n 시점엔 academy 가 없어 언어팩이 비어 있다.
    // 로그인 완료 후 받아온 academy 의 언어팩을 i18next 에 주입한다.
    // 후속 화면이 빈 리소스로 mount 되어 키가 노출되는 race 를 막기 위해 await 한다.
    await setI18nLanguagePack(academy.languagePackFileUrl)
    const _student = {
      ...student.data,
      appSetting,
    }

    commonRepo.setStudent(_student)
    commonRepo.setCurriculumCore(curriculumCore)
    const isSchoolflat =
      envUtils.isSchool || !!(academy.customerType && academy.customerType.includes('B2G'))

    // restrictions 출처 우선순위:
    //   1. login / onReissueSuccess 응답 (param 으로 전달됨, 가장 최신)
    //   2. me.restrictions (새로고침 시 login response를 담은 스토어 휘발됨)
    commonRepo.setPermission(
      new UserPermission({
        userRole: me.userType === 'PARENT' ? 'PARENT' : 'STUDENT',
        academyAuthority: academy.authority,
        subscribeItems: academy.subscribeItems,
        isSchoolflat,
        restrictions: restrictions ?? me.restrictions ?? [],
      }),
    )

    // SELF_LEARNING restriction 차단 시엔 보충학습 관련 remote-storage 도 조회 X
    if (commonRepo.permission.byFeature.isAllowed('SELF_LEARNING')) {
      remoteStorageService.get('isVisitedSelfLearning')
      remoteStorageService.get('isCompletedSelfLearning')
    }

    // 데이터 갱신될 때마다해야 정확하지만 일단 초기 데이터에만 넣어주기
    datadogClient.setUser({
      ..._student,
      // 학부모/학생 분기 해주어야 할지?
      userType: 'student',
    })

    datadogClient.setAccount({
      ...academy,
      id: academy.academyId,
    })

    datadogClient.setGlobalContextProperty('academy', academy)
    datadogClient.setGlobalContextProperty('student', _student)
    commonRepo.clearLoadFailure()
  } catch (e) {
    console.error(e)
    commonRepo.setLoadFailure(e instanceof Error ? e.message : 'unknown error')
  }
}
